// Ultimate Card Game (UCG) - Bot AI System
//
// Plays the full active roster of one race (DECKS_DATA "Human"/"Sul") with a
// context-aware utility scorer over all 5 hand slots (evaluateHandUtility()),
// a multi-action budget per decision tick, a WavePlanner
// (js/engine/ai/wavePlanner.js) that decides whether this tick may play at
// all (stream / build up a wave / release it / defend), and a one-time
// tactical superweapon run by NukeController (js/engine/ai/nukeController.js),
// whose plan each tick may also hold back plasma or plays. Everything that
// differs between difficulties is data in js/engine/ai/profiles.js,
// including an openly declared income bonus (Insane). On top of the tier, a
// hidden per-match doctrine (js/data/aiDoctrines.js) flavours deck, card
// preferences, wave pacing and superweapon patience - never shown to the
// player. Current behaviour, measurements and
// the planned rework: docs/AI_OPPONENT_ANALYSIS_AND_PLAN_2026-09-22.md and
// docs/AI_OPPONENT_IMPLEMENTATION_PLAN_2026-09-22.md.
//
// A humanizer layer (js/engine/ai/humanizer.js) makes the bot react to a
// slightly old picture of the enemy, decide on an irregular rhythm, think
// twice before expensive plays, get rattled by setbacks and (Easy/Normal) slip.
//
// Offline only: js/main.js gates every BotAI.update() behind !isOnline and
// !isCampaignMission. Not routed through CommandQueue/CommandProcessor for
// that reason - it calls the same CardSystem/Economy methods the player's
// commands call and shares the detonation itself via triggerSuperweapon(),
// so no game logic is duplicated - just wiring.
//
// Superweapon charges come from a shared ledger (createChargeLedger()) so
// the game, the HUD and both bots see one truth - including, later, the
// opponent's charge. A bot constructed without one (unit-test mocks) keeps
// a private ledger of its own.
//
// One BotAI instance can outlive many matches (Game creates the skirmish bot
// once at boot), so every per-match field must be cleared in
// resetForMatch(), which the match start paths call.

import { triggerSuperweapon } from '../commands/commandProcessor.js';
import { UnseededRNG } from './rng.js';
import { ALL_TAUNT_IDS } from '../data/taunts.js';
import { BALANCE } from '../data/balance.js';
import { NukeController } from './ai/nukeController.js';
import { WavePlanner } from './ai/wavePlanner.js';
import { getDifficultyProfile } from './ai/profiles.js';
import { AI_DOCTRINES, WAVE_THRESHOLD_CORRIDOR, buildDeckFor, pickDoctrineId } from '../data/aiDoctrines.js';
import { generateProceduralAiDeck } from '../data/aiDecks.js';
import { aiAdaptiveMemory } from './ai/adaptiveMemory.js';
import { aiOpponentMemory } from './ai/opponentMemory.js';
import { BoardView, Belief, scanThreats } from './ai/perception.js';
import { evaluateHandUtility as scoreHand } from './ai/handScorer.js';
import { shouldAbortPendingCombo } from './ai/stateProjector.js';
import { Strategist } from './ai/strategist.js';
import { Humanizer } from './ai/humanizer.js';

// A tier's wave config as flavoured by a doctrine: the base threshold is
// scaled but kept inside the measured corridor, and only where the tier
// saves into waves at all (streaming tiers stay streaming).
function doctrineWaves(waves, doctrine) {
  if (!waves) return null;
  const mod = doctrine.waves || {};
  const threshold = Math.min(WAVE_THRESHOLD_CORRIDOR.max, Math.max(WAVE_THRESHOLD_CORRIDOR.min, waves.threshold * (mod.thresholdMul ?? 1)));
  return { ...waves, threshold, defendFactor: waves.defendFactor * (mod.defendFactorMul ?? 1) };
}

const GAME_SPEED_MULTIPLIER = 1.4;

// Superweapon charge ledger over a { western, eastern } charges object that
// is re-read on every access - Game replaces its superweaponCharges object
// per match, so the getter must never capture a stale one.
export function createChargeLedger(getCharges) {
  return {
    get(team) {
      const charges = getCharges();
      return Math.max(0, charges?.[team] ?? 0);
    },
    set(team, value) {
      const charges = getCharges();
      if (charges) charges[team] = value;
    },
    consume(team) {
      const charges = getCharges();
      if (!charges || !(charges[team] > 0)) return false;
      charges[team] -= 1;
      return true;
    }
  };
}

// Private fallback ledger for a bot with no shared source; the only kind
// resetForMatch() refills, since a shared one is owned (and reset) by
// whoever created it.
function createLocalChargeLedger() {
  const charges = { western: BALANCE.superweaponChargesPerMatch, eastern: BALANCE.superweaponChargesPerMatch };
  const ledger = createChargeLedger(() => charges);
  ledger.reset = (team) => { charges[team] = BALANCE.superweaponChargesPerMatch; };
  return ledger;
}

export class BotAI {
  constructor(economy, cardSystem, battlefield, particles, audio, difficulty = 'medium', team = 'eastern', rng = null, options = {}) {
    this.economy = economy;
    this.cardSystem = cardSystem;
    this.battlefield = battlefield;
    this.particles = particles;
    this.audio = audio;
    this.difficulty = difficulty;

    this.team = team || 'eastern';
    this.rng = rng || battlefield?.rng || UnseededRNG;

    if (this.cardSystem) {
      if (this.team === 'eastern') this.cardSystem.aiEastern = this;
      else if (this.team === 'western') this.cardSystem.aiWestern = this;
    }

    this.superweapons = options.superweapons || createLocalChargeLedger();

    // Original: 1 tick per 1.0 game second at Faster speed
    this.baseInterval = 1.0 / GAME_SPEED_MULTIPLIER; // ~0.714s real
    this.decisionTimer = 0;

    // Total real game-seconds elapsed while this AI has been active in the
    // current match - reset by resetForMatch().
    this.gameTime = 0;
    // Superweapon decisions (when to fire, bank, hold, flood) - profile set
    // by setDifficulty() below.
    this.nuke = new NukeController(this, getDifficultyProfile(difficulty).nuke);
    // Whether and when this tick may play cards at all - config set by
    // setDifficulty() below.
    this.waves = new WavePlanner(this, null);
    // Hidden play style for the current match - selectDoctrine() rolls it
    // before the deck is dealt; neutral until then.
    this.doctrineId = 'standard';
    this.doctrine = AI_DOCTRINES.standard;
    this.hasWinStreakRelief = false;
    this.flexSlotsCount = 0;

    // Slot age tracker for gentle anti-starvation hand rotation
    this.slotAges = [0, 0, 0, 0, 0];

    // Rolling memory of recent enemy threats (air, cloak, heavy armor) for proactive counterplay
    this.enemyMemory = {
      recentAirTime: -999,
      recentCloakTime: -999,
      recentHeavyArmorTime: -999,
      observedUnitIds: new Set(),
      observedCardNames: [],
      lastObservedPlay: null,
      pendingComboObservations: [],
      observedPlayerCombos: []
    };
    this.liveOpponentProfile = null;
    this.pendingCombo = null;
    // What the bot may know about the opponent beyond the board (plasma, hand).
    this.belief = new Belief(this);
    // Hidden match plans (js/engine/ai/strategist.js); a profile switches them on.
    this.strategist = new Strategist(this);
    // Perception lag, rhythm, hesitation, mood and slips (js/engine/ai/humanizer.js); a profile switches them on.
    this.humanizer = new Humanizer(this);
    this._humanInterval = null;

    // Very rare, purely cosmetic "personality" taunt - independent of every
    // tactical decision above, never touches sim state (see
    // docs/PLAYER_TAUNTS_SPEC.md section 5A). Deliberately rolled with
    // Math.random() rather than this.rng: the bot only ever runs offline
    // (js/main.js gates all BotAI.update() calls behind !isOnline), so there
    // is no determinism to protect, and reusing the sim RNG here would still
    // perturb the deterministic stream card decisions and spawn offsets rely
    // on for no benefit. Draws from ALL_TAUNT_IDS (every taunt that exists),
    // not a human player's own unlocked/equipped set - the bot has no
    // inventory and isn't gated by the Slot Machine unlock economy.
    this.tauntChancePerDecision = 0.0025; // ~0.25% per decision tick
    this.minTauntTime = 20; // seconds of game time before the bot may taunt at all
    this.tauntCooldownSec = 75; // minimum spacing between the bot's own taunts
    this._nextTauntAllowedAt = 0;
    this.pendingTaunt = null; // tauntId awaiting pickup by the game loop
    this._deckChosenForMatch = false;
    this.archetypeId = null;
    this.archetypeName = null;
    this.isHybridDeck = false;
    this.counterThreat = null;
    this.optimizationFactor = 0;
    this._lastPlayGameTime = 0;

    this.setDifficulty(difficulty);
  }

  // Read-compatible views of this bot's own charge in the shared ledger
  // (older code and tests used these as plain fields).
  get superweaponCharges() {
    return this.superweapons.get(this.team);
  }

  set superweaponCharges(value) {
    this.superweapons.set(this.team, value);
  }

  get nukesAvailable() {
    return this.superweapons.get(this.team);
  }

  set nukesAvailable(value) {
    this.superweapons.set(this.team, value);
  }

  // Clears every per-match field. Must run at each match start: the
  // skirmish bot lives for the whole session, and a gameTime carried over
  // from the previous match used to disable the early-nuke gate from the
  // first Rematch on (the bot could nuke in its first seconds).
  resetForMatch() {
    this.gameTime = 0;
    this.decisionTimer = 0;
    this._lastPlayGameTime = 0;
    this.nuke.reset();
    this.waves.reset();
    this.applyIncomeMultiplier();
    this.slotAges = [0, 0, 0, 0, 0];
    this.enemyMemory = {
      recentAirTime: -999,
      recentCloakTime: -999,
      recentHeavyArmorTime: -999,
      observedUnitIds: new Set(),
      observedCardNames: [],
      lastObservedPlay: null,
      pendingComboObservations: [],
      observedPlayerCombos: []
    };
    this.liveOpponentProfile = null;
    this.pendingCombo = null;
    this.belief.reset();
    this._nextTauntAllowedAt = 0;
    this.pendingTaunt = null;
    // The deck decision (archetype, counter-drafting, win-streak relief) is
    // made by selectDoctrine() BEFORE this reset at every match start, and is
    // what telemetry reports afterwards: only a reset that was not preceded
    // by a deck choice for this match clears it, so nothing stale survives.
    if (this._deckChosenForMatch) this._deckChosenForMatch = false;
    else this._clearDeckDecision();
    this.superweapons.reset?.(this.team);
    // The deck is dealt and the opponent profile known: draw the opening plan.
    this.strategist.begin();
    this.humanizer.reset();
    this._humanInterval = null;
  }

  _clearDeckDecision() {
    this.archetypeId = null;
    this.archetypeName = null;
    this.subArchetypeId = null;
    this.isHybridDeck = false;
    this.counterThreat = null;
    this.optimizationFactor = 0;
    this.hasWinStreakRelief = false;
    this.flexSlotsCount = 0;
  }

  setDifficulty(diff) {
    this.difficulty = diff;
    this.applyProfile(getDifficultyProfile(diff));
  }

  // Applies a difficulty profile (js/engine/ai/profiles.js). Separate from
  // setDifficulty() so calibration tooling (tools/ai-eval/) can run a tier
  // with individual values overridden.
  applyProfile(profile) {
    this.profile = profile;
    const doctrine = this.doctrine || AI_DOCTRINES.standard;
    const thetaOffset = doctrine.nuke?.thetaOffset || 0;
    this.nuke?.setProfile(thetaOffset ? { ...profile.nuke, theta: profile.nuke.theta + thetaOffset } : profile.nuke);
    this.waves?.setConfig(doctrineWaves(profile.waves, doctrine));
    this.strategist?.setConfig(profile.strategist);
    this.humanizer?.setConfig(profile.humanizer);
    this._humanInterval = null;
    this.intervalMultiplier = profile.decisionIntervalMultiplier;
    this.counterSensitivity = profile.counterSensitivity;
    this.maxBurstActions = profile.maxBurstActions;
    // Takes effect at once, so switching difficulty mid-match also adds or
    // removes the Insane income bonus immediately.
    this.applyIncomeMultiplier();
  }

  // Rolls this match's hidden doctrine for a deck race and returns the deck
  // spec to deal ({ race, cards }, for CardSystem.setDeck()). Call before
  // the starting hand is drawn. `requestedId` (developer override, eval
  // tooling) wins when valid for the race, otherwise falls back to the
  // neutral 'standard' rather than to a random one, so a measurement never
  // silently runs a different doctrine. `fallbackDeck` is returned unchanged
  // (with the neutral doctrine) when the race has no active roster - an
  // unknown deck id resolves to race 'Random', and a doctrine deck built
  // for it would be empty. `definition` (calibration tooling only) replaces
  // the chosen doctrine's data for this bot.
  selectDoctrine(race, requestedId = null, fallbackDeck = null, definition = null, opponentProfileOverride = undefined) {
    // Start from a clean slate, so a branch that does not set a field (the
    // doctrine-deck branch has no archetype) cannot leave the last match's.
    this._clearDeckDecision();
    this._deckChosenForMatch = true;
    let id;
    if (requestedId) {
      id = AI_DOCTRINES[requestedId]?.races.includes(race) ? requestedId : 'standard';
    } else {
      id = pickDoctrineId(race, this.rng);
    }
    let doctrine = (definition && id === requestedId) ? definition : AI_DOCTRINES[id];

    let deck;
    if (this.profile?.proceduralDeck && !definition) {
      // The session's own picture of the player, else what an earlier session left in the memory.
      const oppProfile = opponentProfileOverride !== undefined
        ? opponentProfileOverride
        : (aiAdaptiveMemory.getPlayerThreatProfile(this.difficulty) ?? this._rememberedProfile());
      this.opponentProfile = oppProfile;
      // The match plan comes first, the deck is built to carry it (js/data/aiDecks.js).
      const gambitId = this.strategist.preview(race, oppProfile);

      // Strategic Doctrine Shift on Consecutive Losses:
      // If the bot lost 2+ matches in a row against a slow, heavy opponent deck (artillery or avgCost >= 16),
      // switch doctrine to aggressive 'rush' to overwhelm their slow setup!
      if (!requestedId && oppProfile?.consecutiveLosses >= 2 && (oppProfile.hasArtillery || (oppProfile.avgCost || 0) >= 16)) {
        id = 'rush';
        doctrine = AI_DOCTRINES.rush;
      }

      deck = generateProceduralAiDeck({
        race,
        doctrineId: id,
        difficulty: this.difficulty,
        rng: this.rng,
        opponentProfile: oppProfile,
        chassisBias: this.profile.chassisBias || 0,
        gambitId
      });
      if (deck?.archetypeId) {
        this.archetypeId = deck.archetypeId;
        this.archetypeName = deck.archetypeName;
        this.subArchetypeId = deck.subArchetypeId || deck.archetypeId;
        aiAdaptiveMemory.recordAiArchetype(this.subArchetypeId);
      }
      this.isHybridDeck = Boolean(deck?.isHybrid);
      this.counterThreat = deck?.counterThreat || null;
      this.optimizationFactor = deck?.optimizationFactor ?? (deck?.counterThreat ? 1.0 : 0.0);
      this.hasWinStreakRelief = Boolean(deck?.hasWinStreakRelief);
      this.flexSlotsCount = deck?.flexSlotsCount || 0;
    } else {
      deck = buildDeckFor(doctrine, race);
      this.isHybridDeck = false;
      this.counterThreat = null;
      this.optimizationFactor = 0;
      this.hasWinStreakRelief = false;
      this.flexSlotsCount = 0;
    }

    if (!deck || !deck.cards || deck.cards.length === 0) {
      id = 'standard';
      doctrine = AI_DOCTRINES.standard;
      deck = buildDeckFor(doctrine, race);
      this.isHybridDeck = false;
      this.counterThreat = null;
      this.hasWinStreakRelief = false;
      this.flexSlotsCount = 0;
    }
    this.doctrineId = id;
    this.doctrine = doctrine;
    this.applyProfile(this.profile);
    if (this.hasWinStreakRelief) {
      // Gently soften counter-sensitivity (0.85x) to widen tactical decision variety
      this.counterSensitivity *= 0.85;
    }
    return deck.cards.length > 0 ? deck : (fallbackDeck ?? race);
  }

  // --- persistent memory of this player (js/engine/ai/opponentMemory.js) ---------

  // Use `memory` for the player `playerKey` from the next match on: the hidden
  // plans' statistics, their habits and (hard tiers) their deck of last time.
  attachOpponentMemory(memory, playerKey) {
    this.opponentMemory = memory;
    this.memoryKey = playerKey;
    this.strategist.memory = memory.gambitMemory(playerKey);
    this.strategist.habits = memory.habitsOf(playerKey);
  }

  detachOpponentMemory() {
    this.opponentMemory = null;
    this.memoryKey = null;
    this.strategist.habits = null;
  }

  // What the memory says about the player's deck, as the threat profile the deck
  // builder reads - only where the tier lets the memory count and has no profile
  // from this session yet. Reads like the match after the first one of a session:
  // half-adapted on Hard, fully on the tiers above.
  _rememberedProfile() {
    if (!this.opponentMemory || (this.profile?.memoryInfluence ?? 0) < 0.5 || this.difficulty === 'easy') return null;
    const deck = this.opponentMemory.rememberedDeck(this.memoryKey);
    if (!deck) return null;
    const a = aiAdaptiveMemory.analyzeDeckThreats(deck);
    return {
      primaryThreat: a.primaryThreat,
      secondaryThreat: a.secondaryThreat,
      isCombinedArms: a.isCombinedArms,
      deckProfile: a.deckProfile || null,
      hasStealth: a.hasStealth,
      hasArtillery: a.hasArtillery,
      hasBoardWipe: a.hasBoardWipe,
      hasSiegeFortress: a.hasSiegeFortress,
      structureCount: a.structureCount,
      avgCost: a.avgCost,
      consecutiveLosses: 0,
      consecutiveWins: 0,
      hasWinStreakRelief: false,
      rotatedFromLoss: false,
      isChampionMimicry: false,
      championDeck: null,
      airRatio: a.airRatio,
      swarmRatio: a.swarmRatio,
      heavyRatio: a.heavyRatio,
      stealthRatio: a.stealthRatio,
      isCrossFactionAllowed: ['hard', 'veryHard', 'insane', 'invincible'].includes(this.difficulty),
      optimizationFactor: this.difficulty === 'hard' ? 0.5 : 1.0,
      recentCards: deck,
      sessionMatchesCount: 0,
      isGracePeriodBurned: false,
      fromMemory: true
    };
  }

  // The match is over: the memory learns the player's deck, what the bot saw
  // them do, and the result.
  recordMatchMemory({ playerCards = [], won = false } = {}) {
    if (!this.opponentMemory) return;
    this.opponentMemory.recordMatch(this.memoryKey, {
      cards: playerCards,
      habits: this.strategist.enabled ? this.strategist.observedHabits() : null,
      tier: this.difficulty,
      won
    });
  }

  // Writes this tier's income factor into the bot's own economy slot. The
  // economy is re-created per match (EconomyEngine.reset(), default 1.0),
  // which is why resetForMatch() re-applies it too.
  applyIncomeMultiplier() {
    const res = this.economy?.resources?.[this.team];
    if (res && this.profile) res.incomeMultiplier = this.profile.incomeMultiplier ?? 1.0;
  }

  update(dt) {
    if (this.battlefield.isGameOver) return;

    this.gameTime += dt;

    const base = this.baseInterval * this.intervalMultiplier;
    this.decisionTimer += dt;
    // A humanized bot decides on an irregular rhythm with the same mean.
    const interval = this.humanizer.active ? (this._humanInterval ??= this.humanizer.nextInterval(base)) : base;

    if (this.decisionTimer >= interval) {
      this.decisionTimer = 0;
      this.makeCardDecision();
      this._humanInterval = this.humanizer.active ? this.humanizer.nextInterval(base) : null;
    }
  }

  // What the plan the bot follows and its mood ask of the wave planner (multipliers, or null).
  waveMods() {
    const plan = this.strategist.waveMods();
    const mood = this.humanizer.waveMods();
    if (!mood) return plan;
    if (!plan) return mood;
    return { thresholdMul: (plan.thresholdMul ?? 1) * mood.thresholdMul, defendFactorMul: (plan.defendFactorMul ?? 1) * mood.defendFactorMul };
  }

  // Analyzes living units on battlefield to compute tactical threat profile
  // (js/engine/ai/perception.js).
  analyzeBattlefieldThreats(enemyTeam) {
    return scanThreats(this, enemyTeam);
  }

  _computeBoardBalanceScore() {
    const b = this.battlefield;
    if (!b) return 0;
    const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
    let ownVal = 0;
    let enemyVal = 0;
    for (const u of (b.units || [])) {
      if (!u || u.isDead) continue;
      const cost = u.cardData?.cost || 6;
      if (u.team === this.team) ownVal += cost;
      else if (u.team === enemyTeam) enemyVal += cost;
    }
    const ownBaseHp = b.bases?.[this.team]?.hp || 1000;
    const enemyBaseHp = b.bases?.[enemyTeam]?.hp || 1000;
    return (ownVal - enemyVal) + 0.15 * (ownBaseHp - enemyBaseHp);
  }

  _observeEnemyPlay(cardName) {
    if (!cardName || !this.enemyMemory) return;
    const now = this.gameTime || 0;
    const last = this.enemyMemory.lastObservedPlay;
    if (last && last.cardName === cardName && Math.abs(now - last.at) < 0.25) return;

    const scoreNow = this._computeBoardBalanceScore();
    if (last && last.cardName !== cardName && (now - last.at) >= 0.1 && (now - last.at) <= 6.0) {
      const delaySec = Number((now - last.at).toFixed(2));
      const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
      const initialCharges = (this.superweapons?.get?.(this.team) || 0) + (this.superweapons?.get?.(enemyTeam) || 0);
      this.enemyMemory.pendingComboObservations.push({
        cardA: last.cardName,
        cardB: cardName,
        delaySec,
        evalAt: now + 15.0,
        baselineScore: last.baselineScore,
        initialCharges
      });
    }
    this.enemyMemory.lastObservedPlay = { cardName, at: now, baselineScore: scoreNow };
  }

  // Utility scoring across all 5 hand slots (js/engine/ai/handScorer.js).
  // `excludedSlots` (optional) holds slots CardSystem already rejected this
  // tick, so a card that can't be played right now doesn't keep winning.
  evaluateHandUtility(hand, threats, currentPlasma, excludedSlots = null) {
    return scoreHand(this, hand, threats, currentPlasma, excludedSlots);
  }

  // Rolls for a rare taunt on every decision tick regardless of what else
  // happens this tick. Result is picked up by the game loop via
  // takePendingTaunt() and forwarded to TauntManager.showTauntBubble().
  maybeTaunt() {
    if (this.pendingTaunt) return;
    if (this.battlefield.isGameOver) return;
    if (this.gameTime < this.minTauntTime || this.gameTime < this._nextTauntAllowedAt) return;
    if (Math.random() >= this.tauntChancePerDecision) return;

    const pool = ALL_TAUNT_IDS;
    this.pendingTaunt = pool[Math.floor(Math.random() * pool.length)];
    this._nextTauntAllowedAt = this.gameTime + this.tauntCooldownSec;
  }

  // Consumes and clears the pending taunt, if any.
  takePendingTaunt() {
    const t = this.pendingTaunt;
    this.pendingTaunt = null;
    return t;
  }

  makeCardDecision() {
    this.maybeTaunt();

    const hand = this.cardSystem.hands[this.team];
    if (!hand) return;

    const enemyTeam = this.team === 'western' ? 'eastern' : 'western';

    // Opening Grace Period: On Easy (or profiles with openingGrace > 0), give new players
    // a calm onboarding window to read their cards and orient before the bot deploys units,
    // unless the player attacks first (reactive wake-up).
    const openingGrace = this.profile?.openingGrace ?? (this.difficulty === 'easy' ? 10 : 0);
    if (openingGrace > 0 && this.gameTime < openingGrace) {
      const playerHasPresence =
        (this.battlefield?.units || []).some(u => u && !u.isDead && !u.isCinematic && !u.isGoldenStatue && u.team === enemyTeam) ||
        (this.battlefield?.structures || []).some(s => s && !s.isDead && !s.isCinematic && !s.isGoldenStatue && s.team === enemyTeam);
      const ownBase = this.battlefield?.bases?.[this.team];
      const isBaseAttacked = ownBase ? (ownBase.hp < (ownBase.maxHp || 1500)) : false;
      if (!playerHasPresence && !isBaseAttacked) {
        return;
      }
    }

    // Superweapon first: the controller may fire, keep our plays out of our
    // own warhead's flight, release a post-impact flood, or ask to hold
    // plasma back (arming bank / insurance) for the rest of this tick.
    // One board picture for everything that decides before the first play of
    // this tick (js/engine/ai/perception.js).
    const view = new BoardView(this);
    this._lastDecisionView = view;
    this.humanizer.observe(view);
    this.strategist.tick(view);
    let plan = this.nuke.tick(view);
    // A value strike waits a moment (a base-saving one never does).
    if (plan.mode === 'fire' && this.humanizer.active && this.humanizer.nukeHold(this.nuke.isCritical(this.nuke.lastAssessment))) plan = { mode: 'normal', reserve: 0 };
    else if (plan.mode !== 'fire') this.humanizer.nukeIdle();
    if (plan.mode === 'fire') {
      if (this.superweapons.consume(this.team)) {
        triggerSuperweapon({ battlefield: this.battlefield, particles: this.particles, audio: this.audio }, this.team, plan.x, plan.y);
        this.nuke.onFired();
        return;
      }
    }
    const reserve = plan.reserve || 0;
    const threats = this.humanizer.reactionThreats(this.analyzeBattlefieldThreats(enemyTeam));
    const baseHpFraction = (this.battlefield.bases[this.team]?.hp || 1000) / (this.battlefield.bases[this.team]?.maxHp || 1000);

    // Evaluate pending enemy combo observations (Säule C: 3-stage causal filter)
    if (this.enemyMemory?.pendingComboObservations?.length) {
      const obsList = this.enemyMemory.pendingComboObservations;
      const currentScore = this._computeBoardBalanceScore();
      const currentCharges = (this.superweapons?.get?.(this.team) || 0) + (this.superweapons?.get?.(enemyTeam) || 0);
      for (let i = obsList.length - 1; i >= 0; i--) {
        const obs = obsList[i];
        if (this.gameTime >= obs.evalAt) {
          obsList.splice(i, 1);
          const superweaponFired = currentCharges < obs.initialCharges;
          const opponentGain = obs.baselineScore - currentScore;
          if (!superweaponFired && opponentGain >= 30) {
            this.enemyMemory.observedPlayerCombos.push([obs.cardA, obs.cardB, obs.delaySec, Number(opponentGain.toFixed(1))]);
            if (this.opponentMemory && this.memoryKey) {
              this.opponentMemory.recordObservedCombo(this.memoryKey, obs.cardA, obs.cardB, obs.delaySec, opponentGain, { superweaponFired });
            }
          }
        }
      }
    }

    // Two-phase pendingCombo state machine (Säule A & Leitplanke 1)
    if (this.pendingCombo) {
      const stillHasB = hand.some(c => c && c.name === this.pendingCombo.cardB);
      const currentTotalPlasma = this.economy.resources[this.team]?.plasma || 0;
      if (!stillHasB || currentTotalPlasma >= 138 || this.gameTime > this.pendingCombo.expiresAt || shouldAbortPendingCombo(this.pendingCombo, threats, baseHpFraction)) {
        this.pendingCombo = null;
      }
    }

    if (this.pendingCombo && this.pendingCombo.phase === 'WAITING_FOR_B_WINDOW') {
      if (this.gameTime >= this.pendingCombo.executeAt) {
        const bSlot = hand.findIndex(c => c && c.name === this.pendingCombo.cardB);
        if (bSlot !== -1) {
          const costB = this.cardSystem.getEffectiveCost(this.team, hand[bSlot]);
          const currentTotalPlasma = this.economy.resources[this.team]?.plasma || 0;
          if (currentTotalPlasma >= costB) {
            if (this.cardSystem.playCard(this.team, bSlot)) {
              this._lastPlayGameTime = this.gameTime;
              this.slotAges[bSlot] = 0;
              this.humanizer.settle(bSlot);
              this.strategist.onPlay(hand[bSlot]);
              this.pendingCombo = null;
              return;
            }
          }
        }
      }
    }

    let comboHoldPlays = false;
    if (this.pendingCombo) {
      if (this.pendingCombo.phase === 'HOLDING_FOR_A') {
        const currentTotalPlasma = this.economy.resources[this.team]?.plasma || 0;
        if (currentTotalPlasma < this.pendingCombo.targetPlasma && currentTotalPlasma < 138) {
          comboHoldPlays = true;
        }
      } else if (this.pendingCombo.phase === 'WAITING_FOR_B_WINDOW') {
        comboHoldPlays = true;
      }
    }

    // Timing: stream, defend, build up a wave (draws only) or release it.
    // Reactions see the enemy board as it was a moment ago; plasma, hand and plays stay current.
    const wave = this.waves.tick(reserve, this.humanizer.reactionView(view));
    const holdPlays = plan.mode === 'hold' || comboHoldPlays || (!wave.allowPlays && plan.mode !== 'flood');
    const burstCap = wave.burstCap ? Math.max(this.maxBurstActions, wave.burstCap) : this.maxBurstActions;

    // Age occupied slots for rotation
    for (let i = 0; i < hand.length; i++) {
      if (hand[i]) this.slotAges[i] = (this.slotAges[i] || 0) + 1;
      else this.slotAges[i] = 0;
    }

    // Dynamic Multi-Action Budget based on current plasma surplus & difficulty
    let currentPlasma = this.economy.resources[this.team]?.plasma || 0;
    let actionsBudget = 1;

    if (plan.mode === 'flood') {
      // Our wipe just landed: spend the bank before the opponent rebuilds.
      actionsBudget = hand.length * 2;
    } else if (currentPlasma >= 100) {
      actionsBudget = Math.min(4, burstCap + 1);
    } else if (currentPlasma >= 45 || (wave.posture === 'push' && currentPlasma >= 18)) {
      actionsBudget = Math.min(3, burstCap);
    } else if (wave.posture === 'defend' && wave.burstCap && currentPlasma >= 14) {
      actionsBudget = Math.min(2, wave.burstCap);
    } else {
      actionsBudget = this.profile.minActionsPerTick;
    }

    let actionTaken = false;
    let prePlayComboDrawDone = false;
    // Slots CardSystem refused this tick (e.g. a Structure whose twin is
    // still standing) - skipped by the scorer for the rest of the tick
    // instead of burning the whole budget on the same rejected card.
    const rejectedSlots = new Set();

    while (actionsBudget > 0) {
      // Plasma this tick may spend: everything above the controller's reserve.
      const totalPlasma = this.economy.resources[this.team]?.plasma || 0;
      currentPlasma = Math.max(0, totalPlasma - reserve);
      const evaluated = this.evaluateHandUtility(hand, threats, currentPlasma, rejectedSlots);
      const { bestOverall } = evaluated;
      let bestAffordable = this.humanizer.choose(evaluated);

      // Pre-Play Tactical Combo Draw: if we have an empty slot, hold a fusion pair (or Pulp wildcard pair),
      // and have surplus plasma to draw BEFORE playing without starving our best play, draw first to complete
      // the Triplet Drilling / Hyper Fusion immediately!
      if (!prePlayComboDrawDone && !holdPlays && this.profile.holdTriplets && wave.posture !== 'defend') {
        const emptySlotsNow = [];
        const countsNow = {};
        let hasFusibleWithPulpNow = false;
        for (let s = 0; s < hand.length; s++) {
          const c = hand[s];
          if (c === null) emptySlotsNow.push(s);
          else if (c && !c.hidden && !c.pending) {
            countsNow[c.name] = (countsNow[c.name] || 0) + 1;
            if (c.name !== 'Pulp' && this.cardSystem?.getFusionInfo?.(c, this.team)) {
              hasFusibleWithPulpNow = true;
            }
          }
        }
        const hasActiveFusionPair = Object.entries(countsNow).some(([n, cnt]) => cnt >= 2 && n !== 'Pulp') ||
          ((countsNow['Pulp'] || 0) >= 1 && hasFusibleWithPulpNow);
        const drawCost = this.cardSystem.getEffectiveDrawCost ? this.cardSystem.getEffectiveDrawCost(this.team) : (this.cardSystem.drawCost || 2);
        const neededForPlay = bestAffordable ? bestAffordable.cost : 0;
        if (
          emptySlotsNow.length > 0 &&
          hasActiveFusionPair &&
          currentPlasma >= neededForPlay + drawCost + 4 &&
          (drawCost === 0 || this.economy.canAffordCard(this.team, drawCost))
        ) {
          prePlayComboDrawDone = true;
          const targetSlot = this.rng.pick(emptySlotsNow);
          if (drawCost > 0) this.economy.spendPlasma(this.team, drawCost);
          this.cardSystem.drawCard(this.team, targetSlot);
          this.slotAges[targetSlot] = 0;
          actionTaken = true;
          continue; // Re-evaluate hand immediately (in case a triplet melt triggered or a better card arrived)
        }
      }

      // Intelligent Saving Guard: if a top-priority heavy card (e.g. Panzer) is almost affordable,
      // don't waste funds spamming cheap units right before we can afford the key counter!
      let shouldSave = false;
      if (bestOverall && bestAffordable && bestOverall !== bestAffordable && this.profile.savingGuard) {
        const gasDiff = bestOverall.cost - currentPlasma;
        if (gasDiff > 0 && gasDiff <= 4 && bestOverall.score >= bestAffordable.score + 20 && currentPlasma < 80) {
          shouldSave = true;
        }
      }

      // Combo Plasma Saving & State Initiation (Säule A)
      const bc = this._lastComboEval?.bestCombo;
      const lookaheadWeight = typeof this.profile?.comboLookahead === 'number'
        ? this.profile.comboLookahead
        : (this.profile?.comboLookahead === false ? 0 : 1.0);
      if (!this.pendingCombo && bc && lookaheadWeight >= 0.5) {
        if (bc.netRequiredPlasma > currentPlasma + 2 && !shouldAbortPendingCombo({ phase: 'HOLDING_FOR_A' }, threats, baseHpFraction)) {
          this.pendingCombo = {
            phase: bc.isVirtualA ? 'WAITING_FOR_B_WINDOW' : 'HOLDING_FOR_A',
            cardA: bc.cardA,
            cardB: bc.cardB,
            slotA: bc.slotA,
            slotB: bc.slotB,
            targetPlasma: bc.netRequiredPlasma,
            optimalDelaySec: bc.optimalDelaySec,
            startedAt: this.gameTime,
            executeAt: bc.isVirtualA ? this.gameTime + bc.optimalDelaySec : 0,
            expiresAt: this.gameTime + 7.0,
            isEconomyB: bc.isEconomyB,
            contextTag: bc.contextTag
          };
          if (!bc.isVirtualA) {
            shouldSave = true;
          }
        }
      }

      // Drilling / Hyper Hunt Guard: if the candidate play would break an intact pair of fusible
      // cards (or a Pulp + fusible card wildcard pair) while an empty slot exists to draw the 3rd,
      // hold back unless we are in an active wave release or under critical defense pressure.
      if (!shouldSave && bestAffordable && this.profile.holdTriplets && wave.posture !== 'push' && wave.posture !== 'defend') {
        const isCriticalDefense = threats.isPressedBack || threats.enemyNearBaseCount >= 2 || (threats.enemyAirUnits > 0 && threats.ownAirTargeters < threats.enemyAirUnits);
        if (!isCriticalDefense) {
          const candCard = bestAffordable.card;
          const nameCount = hand.filter(c => c && !c.hidden && !c.pending && c.name === candCard.name).length;
          const pulpInHand = hand.filter(c => c && !c.hidden && !c.pending && c.name === 'Pulp').length;
          const fusibleNonPulpInHand = hand.some(c => c && !c.hidden && !c.pending && c.name !== 'Pulp' && this.cardSystem?.getFusionInfo?.(c, this.team));
          const hasEmptySlot = hand.some(c => c === null);
          const isProtectedPair = (nameCount === 2 && this.cardSystem?.getFusionInfo?.(candCard, this.team)) ||
            (pulpInHand >= 1 && candCard.name !== 'Pulp' && this.cardSystem?.getFusionInfo?.(candCard, this.team)) ||
            (candCard.name === 'Pulp' && pulpInHand <= 2 && fusibleNonPulpInHand);
          if (isProtectedPair && hasEmptySlot) {
            shouldSave = true; // Protect the pair and fall through to draw into an empty slot
          }
        }
      }

      // Emergency Handlock Breaker:
      // If the hand is completely full (no empty slots to draw into) and no card scored > 0,
      // do not deadlock forever! Pick the best affordable playable non-structure combat unit
      // to establish frontline presence, free up a hand slot, and allow drawing new cards.
      let isEmergencyHandlockPlay = false;
      if ((!bestAffordable || bestAffordable.score <= 0) && !holdPlays && hand.every(c => c !== null)) {
        const emergencyCand = evaluated.candidates.find(c =>
          c.isAffordable &&
          c.score > -500 && // excludes strictly forbidden cards (-999, e.g. Claiming Pillar or invalid spells)
          !c.card.isStructure &&
          !c.card.isSpell
        );
        if (emergencyCand) {
          bestAffordable = emergencyCand;
          isEmergencyHandlockPlay = true;
        }
      }

      if (bestAffordable && (bestAffordable.score > 0 || isEmergencyHandlockPlay) && !shouldSave && !holdPlays) {
        // Tactical Pacing: prevent relentless streaming on Easy when battlefield is calm.
        const pacingInterval = this.profile?.pacingInterval ?? (this.difficulty === 'easy' ? 3.5 : 0);
        if (pacingInterval > 0 && !isEmergencyHandlockPlay) {
          const isUnderThreat = threats.isPressedBack ||
            (threats.enemyNearBaseCount || 0) >= 1 ||
            (threats.enemyGroundCount || 0) >= 3 ||
            (threats.enemyAirUnits || 0) >= 1 ||
            ((threats.ownBase?.hp || 1500) < (threats.ownBase?.maxHp || 1500));
          const timeSinceLastPlay = this.gameTime - (this._lastPlayGameTime || 0);
          if (!isUnderThreat && this._lastPlayGameTime > 0 && timeSinceLastPlay < pacingInterval) {
            break;
          }
        }

        const playedCard = hand[bestAffordable.slot];
        // Thinks twice before an expensive card - never in defence, flood or planned combos.
        const isPlannedCombo = this.pendingCombo || wave.isOpportunistic || (bc && (playedCard.name === bc.cardA || playedCard.name === bc.cardB)) || (playedCard.name === 'The Salvage Company' && wave.posture === 'push');
        if (!isPlannedCombo && this.humanizer.hesitate(playedCard, bestAffordable.slot, plan.mode === 'flood' ? 'defend' : wave.posture)) {
          if (wave.posture === 'push') this.waves.forceRelease(this.humanizer.pendingHesitationSeconds(bestAffordable.slot) + 0.3);
          rejectedSlots.add(bestAffordable.slot);
          continue;
        }
        if (this.cardSystem.playCard(this.team, bestAffordable.slot) === false) {
          // Rejected: no plasma spent, so no budget or slot age consumed
          // either. Bounded - every retry excludes one more slot.
          rejectedSlots.add(bestAffordable.slot);
          continue;
        }
        this._lastPlayGameTime = this.gameTime;
        this.slotAges[bestAffordable.slot] = 0;
        this.humanizer.settle(bestAffordable.slot);
        this.strategist.onPlay(playedCard);

        // 2-Phase pendingCombo transition (Säule A)
        let comboHoldingWindow = false;
        if (this.pendingCombo && this.pendingCombo.phase === 'HOLDING_FOR_A' && playedCard.name === this.pendingCombo.cardA) {
          const cardBObj = hand.find(c => c && c.name === this.pendingCombo.cardB);
          const costB = cardBObj?.cost || 25;
          const windowDuration = Math.max(4.0, (costB / 2.2) + 2.5);
          this.pendingCombo.phase = 'WAITING_FOR_B_WINDOW';
          this.pendingCombo.executeAt = this.gameTime + this.pendingCombo.optimalDelaySec;
          this.pendingCombo.expiresAt = this.pendingCombo.executeAt + windowDuration;
          comboHoldingWindow = true;
        } else if (!this.pendingCombo && bc && playedCard.name === bc.cardA && !bc.isVirtualA && lookaheadWeight >= 0.5) {
          const cardBObj = hand.find(c => c && c.name === bc.cardB);
          const costB = cardBObj?.cost || 25;
          const remainingPlasma = this.economy.resources[this.team]?.plasma || 0;
          if (remainingPlasma + (bc.optimalDelaySec + 4.5) * 2.2 >= costB) {
            const windowDuration = Math.max(4.0, (costB / 2.2) + 2.5);
            this.pendingCombo = {
              phase: 'WAITING_FOR_B_WINDOW',
              cardA: bc.cardA,
              cardB: bc.cardB,
              slotA: bc.slotA,
              slotB: bc.slotB,
              targetPlasma: bc.netRequiredPlasma,
              optimalDelaySec: bc.optimalDelaySec,
              startedAt: this.gameTime,
              executeAt: this.gameTime + bc.optimalDelaySec,
              expiresAt: this.gameTime + bc.optimalDelaySec + windowDuration,
              isEconomyB: bc.isEconomyB,
              contextTag: bc.contextTag
            };
            comboHoldingWindow = true;
          }
        }

        actionsBudget--;
        actionTaken = true;
        if (comboHoldingWindow) break; // Protect plasma and timing window for card B!
        actionTaken = true;
        if (this.humanizer.spread && actionsBudget > 0 && plan.mode !== 'flood' && wave.posture !== 'defend') { this.humanizer.requestFollowUp(); break; }
        continue;
      }

      // If no card can/should be played: draw into empty slots
      const emptySlots = [];
      for (let s = 0; s < hand.length; s++) {
        if (hand[s] === null) emptySlots.push(s);
      }

      const drawCost = this.cardSystem.getEffectiveDrawCost ? this.cardSystem.getEffectiveDrawCost(this.team) : (this.cardSystem.drawCost || 2);
      if (emptySlots.length > 0 && currentPlasma >= drawCost && (drawCost === 0 || this.economy.canAffordCard(this.team, drawCost))) {
        if (this.humanizer.skipDraw()) break; // forgot to draw
        // Pick empty slot (randomized with RNG to avoid slot-0 bias)
        const targetSlot = this.rng.pick(emptySlots);
        if (drawCost > 0) this.economy.spendPlasma(this.team, drawCost);
        this.cardSystem.drawCard(this.team, targetSlot);
        this.slotAges[targetSlot] = 0;
        actionsBudget--;
        actionTaken = true;
        if (this.humanizer.spread && actionsBudget > 0 && plan.mode !== 'flood' && wave.posture !== 'defend') { this.humanizer.requestFollowUp(); break; }
        continue;
      }

      break; // No further actions possible this tick
    }
  }

  /**
   * Returns anonymized internal AI telemetry state for match-end reporting.
   * Keeps internal properties isolated from UI files and orchestrators.
   */
  getTelemetryMetadata() {
    return {
      doctrineId: this.doctrineId || 'standard',
      optimizationFactor: this.optimizationFactor ?? 0.0,
      threatDetected: this.opponentProfile?.primaryThreat || null,
      hasWinStreakRelief: Boolean(this.hasWinStreakRelief),
      flexSlotsCount: this.flexSlotsCount || 0,
      // Anonymous ids of the hidden plans this match went through (never shown to a player).
      gambits: this.strategist.enabled ? this.strategist.planLog().map(p => p.id) : null
    };
  }
}
